/**
 * The event cover — handoff §2 (Cover) and §4 (geometry, framing).
 *
 * A white box holding the cover photos as absolutely positioned tiles (4px white gutters,
 * 3px on phones), an optional left-to-right white fade for the layouts that put a photo under
 * the text, and — on desktop — the text column on top. Which tiles go where comes from
 * `coverGeom`, the one geometry the page, the review previews and the layout picker share.
 *
 * `editable` turns every tile into a drag target: pointer-down + move rewrites that photo's
 * focal point through `onFocal(id, fx, fy)` (only the cropped axis moves, §4 Framing). The text
 * column lets pointer events through so a drag under the title still reaches the photo.
 *
 * Props
 *   photos      [{ id, src, r, fx, fy }] in cover order (see coverLayout.coverPhotos)
 *   layout      saved layout ('auto' resolves to the suggestion) · split 60 | 50 | 40
 *   mobile      the phone cover: photos only, 300px (380 stacked), text rendered by the caller
 *   width       px width to lay out for (previews); measured with a ResizeObserver otherwise
 *   onOpenPhoto (index) → the media viewer · onRatio(src, r) → a photo's measured shape
 *   children    the desktop text column; a function receives { textPx, titleFs }
 */
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { coverGeom, coverHeight, resolveLayout, titleSize, tilePadding, dragFocal, focalPosition } from './coverLayout';
import { rememberImageRatio } from '../../hooks/useImageRatios';

export const COVER_FADE = 'linear-gradient(90deg, #fff 0%, rgba(255,255,255,.96) 48%, rgba(255,255,255,0) 100%)';
const DESKTOP_MAX = 1280;
const DESKTOP_PAD = 32;

export default function EventCover({
  photos = [],
  layout = 'auto',
  split = 60,
  mobile = false,
  width,
  editable = false,
  onFocal,
  onOpenPhoto,
  onRatio,
  children,
  className = '',
  testId = 'event-cover',
  /** (photo, index, objectPosition) → a node to draw in that tile instead of the <img> (the Pro cover video rides photo 1). Return null for the default image. */
  renderMedia,
  /** (W, layout, mobile) → px; defaults to coverHeight. */
  heightFor,
  /** Padding of the desktop text column (default 40). */
  textPadding = 40,
}) {
  const ref = useRef(null);
  const [measured, setMeasured] = useState(0);
  useLayoutEffect(() => {
    if (width) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => { if (el.clientWidth) setMeasured(el.clientWidth); };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [width]);

  // The cover's pixel box: on desktop the main column (≤ 1280 minus the 32px gutters).
  const W = width || measured || (mobile ? 390 : Math.min(DESKTOP_MAX, 1280) - DESKTOP_PAD * 2);
  const resolved = resolveLayout(layout, photos);
  const H = heightFor ? heightFor(W, resolved, mobile) : coverHeight(W, resolved, mobile);
  const geom = coverGeom(resolved, photos, W, H, mobile, split);
  const gap = mobile ? 3 : 4;
  const hasPhotos = photos.length > 0;
  const textPx = Math.max(0, (W * geom.textW) / 100 - 80);
  const titleFs = titleSize(textPx);

  const startDrag = useCallback((photo, e) => {
    if (!editable || !onFocal) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const start = { x: e.clientX, y: e.clientY, fx: photo.fx ?? 50, fy: photo.fy ?? 50 };
    const move = (ev) => {
      const { fx, fy } = dragFocal({ r: photo.r, boxW: rect.width, boxH: rect.height, startFx: start.fx, startFy: start.fy, dx: ev.clientX - start.x, dy: ev.clientY - start.y });
      onFocal(photo.id, fx, fy);
    };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); window.removeEventListener('pointercancel', up); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  }, [editable, onFocal]);

  const tiles = geom.tiles.map((t) => {
    const photo = photos[t.i] || photos[0];
    const isMore = t.more > 0;
    const pos = focalPosition(photo);
    const openable = !editable && !!onOpenPhoto;
    const Inner = openable ? 'button' : 'div';
    const innerProps = openable
      ? { type: 'button', onClick: () => onOpenPhoto(isMore ? t.i : t.i), 'aria-label': isMore ? `${t.more} more photos` : `Photo ${t.i + 1}` }
      : { onPointerDown: editable && !isMore ? (e) => startDrag(photo, e) : undefined, role: editable ? 'img' : undefined, 'aria-label': `Photo ${t.i + 1}` };
    return (
      <div
        key={`${t.i}-${t.x}-${t.y}`}
        className="absolute box-border bg-white"
        style={{ left: `${t.x}%`, top: `${t.y}%`, width: `${t.w}%`, height: `${t.h}%`, padding: tilePadding(t, gap), touchAction: editable ? 'none' : undefined }}
        data-testid="cover-tile"
        data-photo={photo?.id}
      >
        <Inner
          {...innerProps}
          className="relative block h-full w-full overflow-hidden p-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-black"
          style={{ background: '#111', cursor: editable && !isMore ? 'grab' : openable ? 'zoom-in' : 'default', border: 'none' }}
        >
          {(!isMore && renderMedia?.(photo, t.i, pos)) || (
            <img
              src={photo?.src}
              alt=""
              draggable={false}
              referrerPolicy="no-referrer"
              onLoad={(e) => { const { naturalWidth: w, naturalHeight: h } = e.currentTarget; if (w && h && photo?.src) { rememberImageRatio(photo.src, w / h); onRatio?.(photo.src, w / h); } }}
              className="absolute inset-0 block h-full w-full select-none object-cover"
              style={{ objectPosition: pos, pointerEvents: 'none' }}
            />
          )}
          {isMore && (
            <span className="absolute inset-0 flex flex-col items-center justify-center text-white" style={{ background: 'rgba(0,0,0,.72)' }} data-testid="mosaic-more">
              <span className="font-mono" style={{ fontSize: mobile ? 22 : 30, fontWeight: 800 }}>+{t.more}</span>
              <span className="font-mono font-bold uppercase" style={{ fontSize: mobile ? 8 : 9, letterSpacing: '0.16em' }}>PHOTOS</span>
            </span>
          )}
          {editable && !isMore && !mobile && (
            <span className="pointer-events-none absolute flex items-center gap-[6px] rounded-full bg-black font-mono font-bold uppercase text-white" style={{ right: 10, bottom: 10, padding: '6px 10px', fontSize: 9, letterSpacing: '0.14em' }}>
              <span className="rounded-full" style={{ width: 6, height: 6, background: '#00FFFF' }} />
              PHOTO {t.i + 1} · DRAG TO REFRAME
            </span>
          )}
        </Inner>
      </div>
    );
  });

  if (mobile) {
    if (!hasPhotos) return null;
    return (
      <section ref={ref} className={`relative overflow-hidden bg-white ${className}`} style={{ height: H }} data-testid={testId} data-layout={resolved}>
        {tiles}
      </section>
    );
  }

  const content = typeof children === 'function' ? children({ textPx, titleFs }) : children;
  return (
    <section ref={ref} className={`relative flex overflow-hidden bg-white ${className}`} style={{ minHeight: hasPhotos ? H : undefined }} data-testid={testId} data-layout={hasPhotos ? resolved : 'none'}>
      {tiles}
      {hasPhotos && geom.fadeW > 0 && (
        <div className="pointer-events-none absolute bottom-0 left-0 top-0" style={{ width: `${geom.fadeW}%`, background: COVER_FADE }} aria-hidden="true" />
      )}
      <div
        className="relative box-border flex flex-col"
        style={{ zIndex: 2, width: hasPhotos ? `${geom.textW}%` : '100%', minHeight: hasPhotos ? H : undefined, padding: textPadding, gap: 18, pointerEvents: editable ? 'none' : 'auto' }}
        data-testid="cover-text"
      >
        {content}
      </div>
    </section>
  );
}
